import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, Building2, Hammer, MapPin, CheckCircle } from 'lucide-react';
import { GameCard } from '../components/GameCard';

const featuredGames = [
  {
    title: 'Was kostet dieses Haus?',
    description: 'Schätze den Wert verschiedener Immobilien in Südtirol. Wie gut ist dein Gespür für den Markt?',
    image: 'https://images.unsplash.com/photo-1734169065204-127b2b724c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhbHBpbmUlMjBob3VzZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzQyNTc4NzZ8MA&ixlib=rb-4.1.0&q=85',
    path: '/tippspiele/was-kostet-dieses-haus',
    type: 'estimation',
    featured: true
  },
  {
    title: 'Neubau oder Sanierung?',
    description: 'Finde heraus, welche Option besser zu deiner Situation passt.',
    image: 'https://images.pexels.com/photos/5691521/pexels-photo-5691521.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    path: '/tippspiele/neubau-oder-sanierung',
    type: 'decision'
  },
  {
    title: 'Wohnung kaufen oder mieten?',
    description: 'Die ewige Frage – beantworte sie passend zu deiner Lebenssituation.',
    image: 'https://images.unsplash.com/photo-1705321963943-de94bb3f0dd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwbGl2aW5nJTIwcm9vbSUyMGludGVyaW9yJTIwZGVzaWdufGVufDB8fHx8MTc3NDI1Nzg3OXww&ixlib=rb-4.1.0&q=85',
    path: '/immobilien/wohnung-kaufen-oder-mieten',
    type: 'quiz'
  },
  {
    title: 'Wohnen in Südtirol',
    description: 'Welcher Wohnort in Südtirol passt am besten zu dir?',
    image: 'https://images.unsplash.com/photo-1768464395439-25b2ef151f80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxpdGFsaWFuJTIwZG9sb21pdGVzJTIwbW91bnRhaW4lMjB2aWxsYWdlJTIwc2NlbmljfGVufDB8fHx8MTc3NDI1Nzg4MHww&ixlib=rb-4.1.0&q=85',
    path: '/suedtirol/wohnen-in-suedtirol',
    type: 'quiz'
  }
];

const categories = [
  {
    icon: Lightbulb,
    title: 'Tippspiele',
    description: 'Schätzspiele und Quiz rund um Immobilienpreise',
    path: '/tippspiele',
    color: '#F97316'
  },
  {
    icon: Building2,
    title: 'Immobilien',
    description: 'Entscheidungshilfen für Kauf, Miete und Investition',
    path: '/immobilien',
    color: '#0284C7'
  },
  {
    icon: Hammer,
    title: 'Haus & Bau',
    description: 'Ratgeber für Bauen und Sanieren',
    path: '/haus-und-bau',
    color: '#059669'
  },
  {
    icon: MapPin,
    title: 'Südtirol',
    description: 'Regionale Tipps zum Wohnen in Südtirol',
    path: '/suedtirol',
    color: '#7C3AED'
  }
];

const benefits = [
  'Spielerisch Wissen aufbauen',
  'Bessere Entscheidungen treffen',
  'Regionale Expertise für Südtirol',
  'Zeitlos nützliche Inhalte'
];

export default function Home() {
  return (
    <>
      {/* Hero Section */}
      <section 
        className="relative min-h-[85vh] flex items-center"
        data-testid="hero-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/7746922/pexels-photo-7746922.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920')`
          }}
        >
          <div className="hero-overlay absolute inset-0" />
        </div>
        
        <div className="container-wide relative z-10 py-20">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 animate-fadeInUp">
              Willkommen bei tippspiel.it
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-[var(--font-heading)] tracking-tight animate-fadeInUp stagger-1">
              Das Tippspiel für bessere Entscheidungen
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed animate-fadeInUp stagger-2">
              Schätzen, vergleichen, verstehen – spielerisch zur besseren Entscheidung rund um Wohnen, Bauen und Immobilien in Südtirol.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp stagger-3">
              <Link 
                to="/tippspiele"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#0284C7] text-white font-semibold rounded-lg hover:bg-[#0369A1] transition-colors duration-200"
                data-testid="hero-cta-primary"
              >
                Tippspiele entdecken
                <ArrowRight size={20} />
              </Link>
              <Link 
                to="/ratgeber"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-colors duration-200 border border-white/20"
                data-testid="hero-cta-secondary"
              >
                Ratgeber lesen
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Concept Explanation */}
      <section className="section-spacing bg-white" data-testid="concept-section">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6 font-[var(--font-heading)]">
              Warum tippspiel.it?
            </h2>
            <p className="text-lg text-[#475569] leading-relaxed">
              Tippspiel – das klingt nach Fußball? Bei uns geht es um andere Entscheidungen: 
              Wohnen, Bauen, Sanieren, Immobilien. Wir helfen dir spielerisch dabei, 
              bessere Einschätzungen zu treffen und die richtigen Fragen zu stellen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Link
                  key={index}
                  to={category.path}
                  className="group p-6 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] hover:border-[#0284C7]/30 transition-all duration-300 card-hover"
                  data-testid={`category-card-${index}`}
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${category.color}15` }}
                  >
                    <Icon size={24} style={{ color: category.color }} />
                  </div>
                  <h3 className="font-semibold text-lg text-[#0F172A] mb-2 font-[var(--font-heading)]">
                    {category.title}
                  </h3>
                  <p className="text-sm text-[#475569]">
                    {category.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Games */}
      <section className="section-spacing" data-testid="featured-games-section">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-3 font-[var(--font-heading)]">
                Beliebte Tippspiele
              </h2>
              <p className="text-[#475569] max-w-xl">
                Teste dein Wissen und triff bessere Entscheidungen
              </p>
            </div>
            <Link 
              to="/tippspiele"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-[#0284C7] font-medium hover:gap-3 transition-all"
              data-testid="view-all-games-link"
            >
              Alle anzeigen
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="bento-grid">
            {featuredGames.map((game, index) => (
              <GameCard key={index} {...game} />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-spacing bg-[#0F172A]" data-testid="benefits-section">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-[var(--font-heading)]">
                Orientierung statt Rätselraten
              </h2>
              <p className="text-[#94A3B8] text-lg mb-8 leading-relaxed">
                Ob du ein Haus kaufen, eine Wohnung sanieren oder einfach verstehen willst, 
                wie der Immobilienmarkt in Südtirol funktioniert – hier findest du spielerische 
                Wege zur Orientierung.
              </p>
              
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <CheckCircle className="text-[#0284C7] flex-shrink-0" size={20} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1768464395439-25b2ef151f80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxpdGFsaWFuJTIwZG9sb21pdGVzJTIwbW91bnRhaW4lMjB2aWxsYWdlJTIwc2NlbmljfGVufDB8fHx8MTc3NDI1Nzg4MHww&ixlib=rb-4.1.0&q=85"
                  alt="Südtirol Landschaft"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl max-w-xs">
                <div className="text-4xl font-bold text-[#0284C7] mb-1 font-[var(--font-heading)]">
                  100%
                </div>
                <div className="text-[#475569] text-sm">
                  Fokus auf zeitlose, nützliche Inhalte für Südtirol
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing" data-testid="cta-section">
        <div className="container-wide">
          <div className="bg-[#0284C7]/5 border border-[#0284C7]/20 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4 font-[var(--font-heading)]">
              Bereit für dein erstes Tippspiel?
            </h2>
            <p className="text-[#475569] max-w-xl mx-auto mb-8">
              Starte mit unserem beliebtesten Schätzspiel und finde heraus, 
              wie gut du Immobilienpreise in Südtirol einschätzen kannst.
            </p>
            <Link 
              to="/tippspiele/was-kostet-dieses-haus"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#0284C7] text-white font-semibold rounded-lg hover:bg-[#0369A1] transition-colors duration-200"
              data-testid="final-cta-button"
            >
              Jetzt starten
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
